import { useMemo, useState } from 'react';
import { useT } from '../../i18n';
import type { RoleView } from '../../lib/api/platformRoles';
import type { OrganizationSummary } from '../../lib/api/platformOrganizations';

// ─────────────────────────────────────────────────────────────────────
// WHO IS OFFERED THIS ROLE (#535).
//
// A FortiGate-style object picker, and the shape is deliberate rather than
// decorative — it is what makes choosing from a long list survivable:
//
//   • SELECTION IS INDEPENDENT OF THE FILTER. Typing narrows what you can see,
//     never what you have chosen. This is the whole reason the pattern exists:
//     with twenty clubs you find them by name one at a time, and a picker that
//     dropped the previous choice on each keystroke would be unusable.
//   • CLEARING THE SEARCH SHOWS WHAT YOU HAVE STAGED, by background rather than
//     by moving rows around — so the list stays in one order and the eye can
//     check the set at a glance.
//   • STAGED IS NOT ADDED. Clicking a staged row unstages it; «Legg til» is the
//     commit. A mis-click costs nothing before that.
//   • ADDED IS NOT SAVED. The chips are a pending set, removable with an ×, and
//     one «Lagre» writes. That matters here specifically: withdrawing a club
//     whose members still hold the role is REFUSED by the server, and a design
//     where each × was its own write would show that refusal after the change
//     had already half-happened.
//
// THE RADIO IS THE AUTHORITY, not the emptiness of the list. «Selected, nobody
// selected» means offered nowhere — useless but honest — and is a different
// statement from «offered everywhere». See the schema note on RoleAvailability:
// deriving one from the other would silently widen a role's reach.
// ─────────────────────────────────────────────────────────────────────

export interface OrganizationScopePickerProps {
  role: RoleView;
  organizations: OrganizationSummary[];
  busy?: boolean;
  /** The server's refusal, shown verbatim — it names the organizations. */
  notice?: string | null;
  onSave: (input: { availability: 'PLATFORM' | 'SELECTED'; organizationIds: string[] }) => void;
}

export function OrganizationScopePicker({
  role,
  organizations,
  busy = false,
  notice = null,
  onSave,
}: OrganizationScopePickerProps) {
  const t = useT();
  const [availability, setAvailability] = useState<'PLATFORM' | 'SELECTED'>(
    role.availability === 'SELECTED' ? 'SELECTED' : 'PLATFORM',
  );
  /** The pending set — what «Lagre» would send. Starts at what the server has. */
  const [added, setAdded] = useState<string[]>(() => role.organizations.map((o) => o.id));
  /** Chosen but not yet added. Survives every change to `query`. */
  const [staged, setStaged] = useState<string[]>([]);
  const [query, setQuery] = useState('');

  const byId = useMemo(() => new Map(organizations.map((o) => [o.id, o])), [organizations]);

  // ONLY WHAT THE ROLE COULD ACTUALLY BE OFFERED TO. A role marked CLUB-only
  // offered to a centre is refused by the server, so listing centres here would
  // be offering a choice that fails on save.
  const eligible = useMemo(
    () => organizations.filter((o) => role.appliesTo === null || o.kind === role.appliesTo),
    [organizations, role.appliesTo],
  );

  const candidates = useMemo(() => {
    const q = query.trim().toLowerCase();
    return eligible
      .filter((o) => !added.includes(o.id))
      .filter((o) => !q || o.name.toLowerCase().includes(q) || o.slug.toLowerCase().includes(q));
  }, [eligible, added, query]);

  const toggleStaged = (id: string) =>
    setStaged((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const commitStaged = () => {
    setAdded((prev) => [...prev, ...staged.filter((id) => !prev.includes(id))]);
    setStaged([]);
  };

  const savedIds = role.organizations.map((o) => o.id);
  const savedAvailability = role.availability === 'SELECTED' ? 'SELECTED' : 'PLATFORM';
  const setChanged = added.length !== savedIds.length || added.some((id) => !savedIds.includes(id));
  // Under PLATFORM the set is not part of the statement, so a stale pending list
  // is not a change — otherwise the page would offer to «save» a no-op.
  const dirty = availability !== savedAvailability || (availability === 'SELECTED' && setChanged);

  return (
    <div className="mt-3 border-t border-white/10 pt-3" data-testid={`sa-scope-picker-${role.key}`}>
      <p className="font-mono text-[10px] uppercase tracking-wider text-ink-3">
        {t('saScope.title')}
      </p>

      <div className="mt-1.5 flex flex-wrap gap-4">
        {(
          [
            ['PLATFORM', 'saScope.platform'],
            ['SELECTED', 'saScope.selected'],
          ] as const
        ).map(([value, label]) => (
          <label key={value} className="flex items-center gap-1.5 text-xs">
            <input
              type="radio"
              name={`availability-${role.id}`}
              checked={availability === value}
              onChange={() => setAvailability(value)}
              disabled={busy}
              data-testid={`sa-scope-${value.toLowerCase()}-${role.key}`}
            />
            {t(label)}
          </label>
        ))}
      </div>

      {availability === 'SELECTED' && (
        <>
          <div className="mt-2.5 flex flex-wrap items-center gap-2">
            <input
              type="search"
              className="input text-xs grow"
              placeholder={t('saScope.search')}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid={`sa-scope-search-${role.key}`}
            />
            <button
              type="button"
              className="btn-ghost text-[11px]"
              disabled={busy || staged.length === 0}
              onClick={commitStaged}
              data-testid={`sa-scope-add-${role.key}`}
            >
              {staged.length > 0
                ? t('saScope.addCount', { count: staged.length })
                : t('saScope.add')}
            </button>
          </div>

          {/* The count is here because a staged organization can be OUT OF VIEW:
              the filter hides rows, it does not unstage them. Without this the
              only honest way to check the set would be to clear the search. */}
          {staged.length > 0 && (
            <p className="mt-1 text-[11px] text-ink-3" data-testid={`sa-scope-staged-${role.key}`}>
              {t('saScope.staged', { count: staged.length })}
            </p>
          )}

          <ul className="mt-2 max-h-48 overflow-y-auto rounded border border-white/10">
            {candidates.length === 0 && (
              <li className="px-2 py-1.5 text-[11px] text-ink-3">
                {eligible.length === added.length ? t('saScope.allAdded') : t('saScope.noMatch')}
              </li>
            )}
            {candidates.map((o) => {
              const isStaged = staged.includes(o.id);
              return (
                <li key={o.id}>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => toggleStaged(o.id)}
                    aria-pressed={isStaged}
                    className={`flex w-full items-baseline justify-between px-2 py-1.5 text-left text-xs ${
                      isStaged ? 'bg-lime/15' : 'hover:bg-white/5'
                    }`}
                    data-testid={`sa-scope-option-${role.key}-${o.id}`}
                  >
                    <span>{o.name}</span>
                    <span className="text-[10px] text-ink-3">
                      {o.kind === 'CENTRE' ? t('saScope.centre') : t('saScope.club')}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>

          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {added.length === 0 ? (
              // SAID OUT LOUD, because it is the one state somebody could read
              // as «everywhere» and be badly wrong about.
              <span className="text-[11px] text-orange" data-testid={`sa-scope-none-${role.key}`}>
                {t('saScope.none')}
              </span>
            ) : (
              added.map((id) => (
                <span
                  key={id}
                  className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2 py-0.5 text-[11px]"
                  data-testid={`sa-scope-chip-${role.key}-${id}`}
                >
                  {byId.get(id)?.name ?? id}
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => setAdded((prev) => prev.filter((x) => x !== id))}
                    aria-label={t('saScope.remove', { name: byId.get(id)?.name ?? id })}
                    className="text-ink-3 hover:text-rose"
                    data-testid={`sa-scope-remove-${role.key}-${id}`}
                  >
                    ×
                  </button>
                </span>
              ))
            )}
          </div>
        </>
      )}

      {notice && (
        <p
          className="mt-2 rounded border border-rose/20 bg-rose/10 px-2 py-1.5 text-[11px] text-rose"
          data-testid={`sa-scope-notice-${role.key}`}
        >
          {notice}
        </p>
      )}

      <div className="mt-2 flex items-center gap-2">
        <button
          type="button"
          className="btn-lime text-[11px]"
          disabled={busy || !dirty}
          onClick={() =>
            onSave({
              availability,
              // Never sent under PLATFORM — the server refuses ids there rather
              // than ignoring them, which is what keeps «restricted» honest.
              organizationIds: availability === 'SELECTED' ? added : [],
            })
          }
          data-testid={`sa-scope-save-${role.key}`}
        >
          {t('saScope.save')}
        </button>
        {dirty && (
          <span className="text-[11px] text-ink-3" data-testid={`sa-scope-pending-${role.key}`}>
            {availability === 'PLATFORM'
              ? t('saScope.pendingPlatform')
              : t('saScope.pendingSelected', { count: added.length })}
          </span>
        )}
      </div>
    </div>
  );
}
